"use client";

import { InputField, PasswordField } from "@/components/Fields";
import { redirect, useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { signIn, useSession } from "next-auth/react";
import { FcGoogle } from "react-icons/fc";

import BenefitsSidebar from "@/components/BenefitsSidebar";
import Image from "next/image";
import Link from "next/link";
import logo from "@/images/Orange Black.png";
import { useSearchParams } from "next/navigation";

export default function SignUp() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [linkedInUrl, setLinkedInUrl] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const searchParams = useSearchParams();
  const referralSource = searchParams.get("referral");
  const [isVisible, setIsVisible] = useState(false);

  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const { data: session } = useSession();
  const router = useRouter();

  useEffect(() => {
    if (session?.user) {
      router.push("/dashboard");
    }
  }, [session, router]);

  // Animation effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const handleGoogleSignIn = async () => {
    try {
      await signIn("google", {
        callbackUrl: "/dashboard",
      });
    } catch (error) {
      console.error("Google sign-in failed:", error);
      setError("Google sign-in failed. Please try again.");
    }
  };

  const handleSalesablySignUp = async () => {
    try {
      await signIn("salesably", {
        callbackUrl: "/dashboard",
      });
    } catch (error) {
      console.error("Salesably sign-in failed:", error);
      setError("Salesably sign-in failed. Please try again.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setLoading(true);

    if (password.length < 8) {
      setError("Password must be at least 8 characters long.");
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch("/api/auth/sign-up", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name,
          email,
          password,
          referralSource,
          linkedInUrl,
        }),
      });

      if (response.ok) {
        setMessage(
          "Registration successful! Please check your email to verify your account. Be sure to check your spam or promotions folder if you don't see the email."
        );

        setEmail("");
        setPassword("");
        setName("");
        setConfirmPassword("");
        setLinkedInUrl("");
      } else {
        const errorData = await response.json();
        console.error("Sign up failed:", errorData.error);
        setError(errorData.error);
      }
    } catch (error) {
      console.error("Sign up failed:", error);
      setError("An unexpected error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    "Research companies instantly - always FREE",
    "Get 5 free credits for people research",
    "Pipedrive CRM bidirectional sync included",
  ];

  return (
    <div className="flex flex-col min-h-screen lg:flex-row">
      <div className="lg:w-2/5">
        <BenefitsSidebar
          title="Join Every Call Prepared"
          benefits={benefits}
        />
      </div>
      <div className="relative flex items-center justify-center flex-1 p-6 sm:p-10 lg:w-3/5 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-950">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(249,115,22,0.05),transparent_50%)]" />

        <div className="relative w-full max-w-md">
          <div className={`px-8 py-10 bg-white dark:bg-slate-800 rounded-2xl shadow-xl shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-200/50 dark:border-slate-700/50 transition-all duration-700 ease-out ${
            isVisible
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-8'
          }`}>
            {/* Header */}
            <div className="text-center mb-6">
              <Image src={logo} alt="Prep5 Logo" width={140} height={46} className="mx-auto" />
              <h2 className="mt-6 text-2xl font-bold text-gray-900 dark:text-white">
                Create your account
              </h2>
              <p className="mt-2 text-sm text-gray-600 dark:text-slate-400">
                Start closing more deals in under 5 minutes
              </p>
            </div>

            {/* Free Credits Banner */}
            <div className="relative p-4 mb-6 overflow-hidden rounded-xl bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200/50 dark:border-green-800/50">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 p-1.5 rounded-full bg-green-500/10 dark:bg-green-500/20">
                  <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800 dark:text-white">Company enrichment is always FREE</p>
                  <p className="text-xs text-gray-600 dark:text-slate-400 mt-0.5">+ Get 5 free credits for prospect enrichment</p>
                </div>
              </div>
            </div>

            {/* Alerts */}
            {error && (
              <div className="p-4 mb-6 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800" role="alert">
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              </div>
            )}
            {message && (
              <div className="p-4 mb-6 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800" role="alert">
                <p className="text-sm text-green-700 dark:text-green-300">{message}</p>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <InputField
                id="name"
                label="Full Name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
              <InputField
                id="email"
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <div>
                <PasswordField
                  id="password"
                  label="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  showPassword={showPassword}
                  toggleShowPassword={() => setShowPassword(!showPassword)}
                  required
                />
                <p className="mt-1.5 text-xs text-gray-500 dark:text-slate-400">
                  Must be at least 8 characters
                </p>
              </div>
              <PasswordField
                id="confirmPassword"
                label="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                showPassword={showConfirmPassword}
                toggleShowPassword={() => setShowConfirmPassword(!showConfirmPassword)}
                required
              />
              <div>
                <InputField
                  id="linkedinUrl"
                  label="LinkedIn Profile (optional)"
                  type="url"
                  value={linkedInUrl}
                  onChange={(e) => setLinkedInUrl(e.target.value)}
                  placeholder="https://linkedin.com/in/yourprofile"
                />
                <p className="mt-1.5 text-xs text-gray-500 dark:text-slate-400">
                  Helps us generate richer insights
                </p>
              </div>

              {referralSource && (
                <div className="p-3 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800" role="alert">
                  <p className="text-sm text-blue-700 dark:text-blue-300">Referred by: <strong>{referralSource}</strong></p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className={`flex w-full justify-center items-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold text-white shadow-lg transition-all duration-300 ${
                  loading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 shadow-orange-500/25 hover:shadow-orange-500/40"
                } focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600`}
              >
                {loading ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Creating account...
                  </>
                ) : (
                  "Create Free Account"
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-slate-700"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white dark:bg-slate-800 text-gray-500 dark:text-slate-400">or continue with</span>
              </div>
            </div>

            {/* SSO Buttons */}
            <div className="space-y-3">
              <button
                onClick={handleGoogleSignIn}
                className="flex items-center justify-center w-full gap-3 px-4 py-3 text-sm font-semibold text-gray-700 dark:text-slate-200 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl shadow-sm hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors"
              >
                <FcGoogle className="w-5 h-5" />
                Sign up with Google
              </button>
              <button
                type="button"
                onClick={handleSalesablySignUp}
                className="flex items-center justify-center w-full gap-3 px-4 py-3 text-sm font-semibold text-gray-700 dark:text-slate-200 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl shadow-sm hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors"
              >
                <Image src="/favicon-32x32.png" alt="Salesably" width={20} height={20} />
                Sign up with Salesably
              </button>
            </div>

            {/* Footer */}
            <p className="mt-6 text-xs text-center text-gray-500 dark:text-slate-400">
              By signing up, you agree to our{" "}
              <Link href="/terms-of-service" className="text-orange-600 hover:underline dark:text-orange-400">
                Terms
              </Link>{" "}
              and{" "}
              <Link href="/privacy-policy" className="text-orange-600 hover:underline dark:text-orange-400">
                Privacy Policy
              </Link>
            </p>

            <p className="mt-6 text-center text-sm text-gray-600 dark:text-slate-400">
              Already have an account?{" "}
              <Link
                href="/sign-in"
                className="font-semibold text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300"
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
